import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Flex, FlexColumn, Loading } from '@elementary/common-ui/components';
import Table, {
  TableEmptyState,
  ToggleColumnsMenu,
} from '@elementary/common-ui/components/Table';
import useChatContext from '@elementary/common-ui/hooks/useChatContext';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { EDITABLE_ASSET_TYPES } from 'shared/constants';
import BulkEditWizard from 'catalog/components/BulkEditWizard';
import { BulkEditProperty } from 'catalog/components/BulkEditWizard/types';
import { STICKY_SECTION_DEPENDENCIES_HEIGHT } from 'catalog/constants';
import { space } from '@elementary/common-ui/style/utils';
import AssetsTableActionMenu from '../AssetsTableActionMenu';
import useDependenciesCounters from '../TestOverviewSidePanel/hooks/useDependenciesCounters';
import { NormalizedDependency, useDependenciesColumns } from './hooks';
import { DependenciesFilterRow } from './components';
import { DependenciesFilterOptions } from './types';

const DependenciesWrapper = styled(FlexColumn)`
  gap: ${space('xxl')};
  flex: 1;
  max-height: calc(100vh - ${STICKY_SECTION_DEPENDENCIES_HEIGHT}px) !important;
`;

const ActionMenuWrapper = styled(Flex)`
  position: fixed;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.drawer + 2};
`;

const TableHeader = styled(Flex)`
  gap: ${space('xxl')};
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

type Props = {
  modelId: string;
  data: NormalizedDependency[] | undefined;
  loading: boolean;
  resource: {
    type: string;
    name: string;
  };
  filter: string;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  editable?: boolean;
  onShowLatestExecution?: (val: boolean) => void;
};

const DependenciesTable = ({
  modelId,
  data = [],
  loading,
  resource,
  filter,
  setFilter,
  editable,
  onShowLatestExecution,
}: Props) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [excludedItems, setExcludedItems] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [filteredTableData, setFilteredTableData] = useState<
    NormalizedDependency[]
  >([]);
  const [bulkEditWizardEditProperty, setBulkEditWizardEditProperty] = useState<
    BulkEditProperty | undefined
  >();
  const [wrapperRect, setWrapperRect] = useState({ left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  const hasPermission = useEnvPermission();
  const canEdit = hasPermission('EDIT_TEST_CONFIGURATIONS');

  const resourceType = resource.type;
  const { columnArr } = useDependenciesColumns(resourceType, data);

  const { data: counters, isLoading: isFiltersLoading } =
    useDependenciesCounters({ modelId, resource });

  const selectedIds = isSelectAll
    ? excludedItems
      ? filteredTableData
          .map((asset) => asset.id)
          .filter((id) => !excludedItems.includes(id))
      : 'all'
    : selectedItems;

  const selectedAssets =
    selectedIds === 'all'
      ? filteredTableData
      : filteredTableData?.filter((asset) => selectedIds.includes(asset.id));

  const chatContext = useMemo(() => {
    if (selectedAssets.length === 0) return undefined;
    return {
      assetIds: selectedAssets.map((a) => a.id),
      displayLabels: selectedAssets.map((a) => a.name),
    };
  }, [selectedAssets]);

  useChatContext(chatContext);

  const handleToggleColumn = useCallback(
    (newColumns: string[]) => {
      const showLatestExecution = [
        'latestExecutionTimestamp',
        'latestExecutionStatus',
      ].some((key) => newColumns.includes(key));
      onShowLatestExecution?.(showLatestExecution);
    },
    [onShowLatestExecution],
  );

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const updateRect = () => {
      const rect = el.getBoundingClientRect();
      setWrapperRect({ left: rect.left, width: rect.width });
    };

    updateRect();
    const observer = new ResizeObserver(updateRect);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const clearSelections = () => {
    setSelectedItems([]);
    setExcludedItems([]);
    setIsSelectAll(false);
    setBulkEditWizardEditProperty(undefined);
  };

  return (
    <DependenciesWrapper ref={wrapperRef}>
      <TableHeader>
        <DependenciesFilterRow
          filter={filter}
          setFilter={setFilter}
          counters={counters}
          isLoading={loading || isFiltersLoading}
        />
        <ToggleColumnsMenu
          sx={{ mt: '2px' }}
          columns={columnArr}
          localStorageKey={`${resource.name}_dependencies_table`}
          onChange={handleToggleColumn}
        />
      </TableHeader>

      {loading ? (
        <Loading />
      ) : (
        <Table
          idKey="id"
          data={
            filter === DependenciesFilterOptions.WithAccess
              ? data.filter((d) => !d.isRestricted)
              : data
          }
          columns={columnArr}
          isLoading={loading}
          emptyStateRenderer={TableEmptyState}
          localStorageKey={`${resource.name}_dependencies_table`}
          urlParamsPrefix={`${resource.name}_dependencies_table_filters`}
          selectedItems={selectedItems}
          isSelectAll={isSelectAll}
          excludedItems={excludedItems}
          onSelectItem={(items) => setSelectedItems(items)}
          onExcludeItem={(items) => setExcludedItems(items)}
          onSelectAllChange={(val) => setIsSelectAll(val)}
          onFilteredItemsChange={(items) => setFilteredTableData(items)}
          selectionColumn={
            editable && canEdit
              ? (row: NormalizedDependency) => !row.isRestricted
              : false
          }
          bottomScrollPadding={100}
        />
      )}

      {createPortal(
        <ActionMenuWrapper
          style={{ left: wrapperRect.left, width: wrapperRect.width }}
        >
          <AssetsTableActionMenu
            selectedAssets={selectedAssets.map((asset) => asset.id)}
            onClose={clearSelections}
            isEditable={selectedAssets?.every((asset) =>
              EDITABLE_ASSET_TYPES.includes(asset.type),
            )}
            onOpenWizard={(value) => setBulkEditWizardEditProperty(value)}
            showWithoutSelection={false}
          />
        </ActionMenuWrapper>,
        document.body,
      )}

      <BulkEditWizard
        isOpen={!!bulkEditWizardEditProperty}
        editProperty={bulkEditWizardEditProperty}
        onClose={() => setBulkEditWizardEditProperty(undefined)}
        onSubmit={clearSelections}
        modelIds={selectedAssets.map((asset) => asset.id)}
      />
    </DependenciesWrapper>
  );
};

export default DependenciesTable;
